import { type ReactNode, useEffect, useState } from "react";
import { BotIcon, CctvIcon } from "zudoku/icons";

// The exchanges the session panel cycles through. Tool names match the tools
// the MCP Server exposes; the results are illustrative.
const SESSION = [
  {
    prompt: "Which cameras at HQ are offline?",
    tool: "list_devices",
    result: "14 offline · 11 behind switch SW-HQ-03",
  },
  {
    prompt: "Certificates expiring this month?",
    tool: "list_device_certificates",
    result: "38 devices across 4 sites",
  },
  {
    prompt: "Patch firmware on vulnerable devices.",
    tool: "upgrade_devices_firmware",
    result: "Awaiting confirmation · 6 devices",
  },
];

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * Types each prompt out, shows the tool call and its result, then moves on to
 * the next one. With reduced motion, each exchange appears whole.
 */
const SessionPanel = () => {
  const [index, setIndex] = useState(0);
  const [typed, setTyped] = useState(0);
  const current = SESSION[index]!;
  const done = typed >= current.prompt.length;

  useEffect(() => {
    if (!done && prefersReducedMotion()) {
      setTyped(current.prompt.length);
      return;
    }
    const timer = done
      ? setTimeout(() => {
          setIndex((index + 1) % SESSION.length);
          setTyped(0);
        }, 3200)
      : setTimeout(() => setTyped(typed + 1), 38);
    return () => clearTimeout(timer);
  }, [index, typed, done, current.prompt.length]);

  return (
    <div
      className="overflow-hidden rounded-lg border border-(--st-jet) bg-(--st-ink)"
      aria-hidden="true"
    >
      <div className="flex items-center justify-between border-b border-(--st-jet) px-4 py-2.5 text-xs text-(--st-snow)/70">
        <span className="flex items-center gap-2">
          <span className="size-1.5 rounded-full bg-(--st-lime)" />
          Live session
        </span>
      </div>
      <div className="flex items-start px-3 pt-7 pb-5">
        <Node label="AI assistant">
          <BotIcon size={18} className="text-(--st-snow)" />
        </Node>
        <Wire delay={0} />
        <Node label="SecuriThings MCP Server" primary>
          <img src="/securithings-symbol.svg" alt="" className="w-8" />
        </Node>
        <Wire delay={0.4} />
        <Node label="Device fleet">
          <CctvIcon size={18} className="text-(--st-snow)" />
        </Node>
      </div>
      <div className="min-h-24 border-t border-(--st-jet) px-4 py-4 font-mono text-xs leading-relaxed">
        <div className="text-(--st-snow)">
          <span className="text-(--st-snow)/70">&gt; </span>
          {current.prompt.slice(0, typed)}
          <span className="mcp-caret ml-0.5 inline-block h-3.5 w-1.5 translate-y-0.5 bg-(--st-snow)" />
        </div>
        {done && (
          <div key={index} className="mcp-fade-in">
            <div className="text-(--st-snow)/70">
              ↳ <span className="text-(--st-snow)">{current.tool}</span>
            </div>
            <div className="text-(--st-lime)">
              ✓ <span className="text-(--st-snow)">{current.result}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const Node = ({
  label,
  primary = false,
  children,
}: {
  label: string;
  primary?: boolean;
  children: ReactNode;
}) => (
  <div className="flex w-20 shrink-0 flex-col items-center gap-2.5">
    <span
      className={
        primary
          ? "mcp-pulse flex size-13 items-center justify-center rounded-full bg-(--st-snow)"
          : "flex size-11 items-center justify-center rounded-full border border-(--st-jet) bg-(--st-jet)"
      }
    >
      {children}
    </span>
    <span
      className={`text-center text-[11px] leading-tight ${
        primary ? "font-medium text-(--st-snow)" : "text-(--st-snow)/70"
      }`}
    >
      {label}
    </span>
  </div>
);

// A connection between two nodes, with a request travelling one way and a
// response travelling back.
const Wire = ({ delay }: { delay: number }) => (
  <div className="relative mt-[22px] h-px flex-1 bg-(--st-jet)">
    <span
      className="mcp-packet absolute -top-0.5 -ml-1.5 h-[5px] w-3.5 rounded-full bg-(--st-snow)"
      style={{ animationDelay: `${delay}s` }}
    />
    <span
      className="mcp-packet-back absolute -top-0.5 -ml-1 h-[5px] w-2 rounded-full bg-(--st-lime)"
      style={{ animationDelay: `${delay + 0.8}s` }}
    />
  </div>
);

/**
 * The dark banner below the title of the MCP Welcome page, with a headline
 * and the intro passed as children. The headline is styled as one but is not a
 * heading element, so the page keeps a single title.
 */
export const McpHero = ({
  heading,
  children,
}: {
  heading: ReactNode;
  children: ReactNode;
}) => (
  <div className="mcp-hero @container not-prose mt-6 mb-10 overflow-hidden rounded-xl bg-(--st-ink) text-(--st-snow) dark:ring-1 dark:ring-(--st-jet)">
    <div className="grid items-center gap-8 p-6 @sm:p-8 @3xl:grid-cols-2">
      <div className="min-w-0">
        <div className="flex items-center gap-2.5 text-xs font-medium text-(--st-snow)/70">
          <span className="size-2 rounded-full bg-(--st-snow) ring-4 ring-(--st-jet)" />
          SecuriThings MCP Server
        </div>
        <p className="mt-4 text-3xl leading-tight font-bold tracking-tight text-balance">
          {heading}
        </p>
        <div className="[&_p]:mt-4 [&_p]:text-sm [&_p]:leading-relaxed [&_p]:font-light [&_p]:text-(--st-snow)">
          {children}
        </div>
      </div>
      <div className="min-w-0">
        <SessionPanel />
      </div>
    </div>
  </div>
);
